import { ContentWithSidebar, SidebarHandle } from "client/Components";
import {
	MediaQueryKind,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { SharePointFile } from "./SharePointPicker/SharePointPickerOverview";
import { SharePointPickerStore } from "./SharePointPicker/SharepointPickerStore";
import { WebDavPickerStore } from "./WebDavPicker/WebDavPickerStore";
import {
	File as WebDavFile,
	Folder as WebDavFolder,
	EnabledServer as WebDavEnabledServer
} from "./WebDavPicker/ItemSelector";
import { FileTypeFilterModel } from "client/Api/Documents/SharePoint/common";
import { SelectedFolder as SharePointFolder } from "./SharePointPicker/ItemSelector";
import { EnabledView as SharedPointEnabledView } from "client_legacy/Documents/SharePoint/DocumentsViewsPane/types";
import { isNotNil } from "client/Misc/utils";
import { GridItem as WebDavGridItem} from "../WebDav/WebDavOverview";
import { GridItem as SharePointGridItem} from "../SharePoint/SharePointOverView";
import { useUnableToConnectToSharePointDialog } from "../SharePoint/Dialogs/UnableToConnectToSharePointDialog";
import { DocumentStoreType } from "client/Api/Documents/common";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type ItemPickerStoreKind = Exclude<DocumentStoreType, DocumentStoreType.Bookmarks>;

export type ItemPickerOptions = {
	commonOptions: CommonItemPickerOptions;
	sharePointOptions: SharePointItemPickerOptions;
	webDavOptions: WebDavItemPickerOptions;
};

export type WebDavPickerSelection = {
	kind: DocumentStoreType.WebDav;
	items: readonly WebDavGridItem[];
};

export type SharePointPickerSelection = {
	kind: DocumentStoreType.SharePoint;
	items: readonly SharePointGridItem[];
};

export type Selection = WebDavPickerSelection | SharePointPickerSelection;

export type ItemPickerProps = {
	options: ItemPickerOptions;
	location: ItemPickerLocation | undefined;
	onLocationChange: (location: ItemPickerLocation) => void;
	selectedItems?: Selection;
	onItemsSelected?: (selection: Selection) => void;
	shouldOpenFileOnClick?: boolean;
};

export const ItemPicker = (props: ItemPickerProps): ReactElement => {
	const {
		options,
		location,
		onLocationChange,
		selectedItems,
		onItemsSelected,
		shouldOpenFileOnClick = true
	} = props;

	const {
		sharePointOptions,
		webDavOptions,
		commonOptions
	} = options;

	const sidebarStatePersistenceKey: string = useUserScopedKey("item-picker:sidebar");
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);

	const [webDavSideBarContainer, setWebDavSideBarContainer] = useState<HTMLDivElement | null>(null);
	const [sharepointSideBarContainer, setSharepointSideBarContainer] =
		useState<HTMLDivElement | null>(null);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const [UnableToConnectToSharePointDialog, openUnableToConnectToSharePointDialog] = useUnableToConnectToSharePointDialog();

	const { webDav: webDavConfiguration } = documentsConfiguration();

	const sharePointSidebarLoadingFailureHandler = useCallback(
		async (reloadSidebar: () => void): Promise<void> => {
			const result: TryResult<void> = await openUnableToConnectToSharePointDialog({});

			if (result.wasSuccessful) {
				reloadSidebar();
			}
		},
		[openUnableToConnectToSharePointDialog]
	);

	const activeStoreKind: ItemPickerStoreKind = useMemo(
		(): ItemPickerStoreKind => {
			if (!sharePointOptions.isEnabled && !webDavOptions.isEnabled) {
				throw new Error("No configured stores for item picker");
			}

			if(isNotNil(location)){
				return location.kind;
			}

			if(sharePointOptions.isEnabled){
				return DocumentStoreType.SharePoint;
			}

			return DocumentStoreType.WebDav;
		},
		[
			location,
			sharePointOptions.isEnabled,
			webDavOptions.isEnabled
		]
	);

	const sidebarCloseHandler = useCallback(
		() => {
			sidebarRef.current
				?.onClose();
		},
		[]
	);

	const webDavFileSelectionHandler = useCallback(
		(items: readonly WebDavGridItem[]): void => {
			onItemsSelected?.({
				kind: DocumentStoreType.WebDav,
				items: items
			});
		},
		[onItemsSelected]
	);

	const sharePointFileSelectionHandler = useCallback(
		(items: readonly SharePointGridItem[]): void => {
			onItemsSelected?.({
				kind: DocumentStoreType.SharePoint,
				items: items
			});
		},
		[onItemsSelected]
	);
	return (
		<>
			<ContentWithSidebar>
				<ContentWithSidebar.Sidebar
					persistentKeyName={sidebarStatePersistenceKey}
					ref={sidebarRef}
				>
					<div ref={setSharepointSideBarContainer}></div>
					<div ref={setWebDavSideBarContainer}></div>
				</ContentWithSidebar.Sidebar>
				{(webDavSideBarContainer && sharepointSideBarContainer) && (
					<ContentWithSidebar.Content hasNoPadding>
						{webDavConfiguration && webDavOptions.isEnabled && (
							<WebDavPickerStore
								isMobile={isMobile}
								sideBarContainer={webDavSideBarContainer}
								commonOptions={commonOptions}
								enabledServer={webDavOptions.availableDestination}
								onFilesSelect={webDavFileSelectionHandler}
								selectedItems={
									selectedItems?.kind === DocumentStoreType.WebDav
										? selectedItems.items
										: []
								}
								isActive={activeStoreKind === DocumentStoreType.WebDav}
								location={
									location?.kind === DocumentStoreType.WebDav
										? location
										: undefined
								}
								onLocationChange={onLocationChange}
								onSidebarClose={sidebarCloseHandler}
								shouldOpenFileOnClick={shouldOpenFileOnClick}
								webDavConfiguration={webDavConfiguration}
							/>
						)}
						{sharePointOptions.isEnabled && (
							<SharePointPickerStore
								isMobile={isMobile}
								sideBarContainer={sharepointSideBarContainer}
								commonOptions={commonOptions}
								options={sharePointOptions}
								onFilesSelected={sharePointFileSelectionHandler}
								selectedItems={
									selectedItems?.kind === DocumentStoreType.SharePoint
										? selectedItems.items
										: []
								}
								onSidebarLoadingFailure={sharePointSidebarLoadingFailureHandler}
								onLocationChange={onLocationChange}
								isActive={activeStoreKind === DocumentStoreType.SharePoint}
								location={
									location?.kind === DocumentStoreType.SharePoint
										? location
										: undefined
								}
								onSidebarClose={sidebarCloseHandler}
								shouldOpenFileOnClick={shouldOpenFileOnClick}
							/>
						)}

					</ContentWithSidebar.Content>
				)}
			</ContentWithSidebar>
			{UnableToConnectToSharePointDialog}
		</>
	);
};
ItemPicker.displayName="ItemPicker";

export interface CommonItemPickerOptions {
	mode: ItemPickerMode;
	fileTypeFilter?: FileTypeFilterModel;
	creationOptions?: InlineCreationOptions;
}

export interface InlineCreationOptions {
	folder: boolean;
	word?: boolean;
	excel?: boolean;
	powerpoint?: boolean;
	onenote?: boolean;
}

export interface SharePointItemPickerDisabledOptions {
	isEnabled: false;
}

export interface SharePointItemPickerEnabledOptions {
	isEnabled: true;
	shouldShowPersonalViews: boolean;
	availableDestination: SharedPointEnabledView;
}

export type SharePointPickerLocation = {
	kind: DocumentStoreType.SharePoint;
	folder: SharePointFolder;
};

export type WebDavPickerLocation = {
	kind: DocumentStoreType.WebDav;
	folder: WebDavFolder;
};

export type ItemPickerLocation = SharePointPickerLocation | WebDavPickerLocation;

export type SharePointItemPickerOptions = SharePointItemPickerDisabledOptions | SharePointItemPickerEnabledOptions;

export interface SharePointFileSelection {
	kind: "sharepoint";
	items: readonly SharePointFile[];
}

export interface SharePointFolderSelection {
	kind: "sharepoint";
	item: SharePointFolder;
}

export interface WebDavItemPickerDisabledOptions {
	isEnabled: false;
}

export interface WebDavItemPickerEnabledOptions {
	isEnabled: true;
	availableDestination: WebDavEnabledServer;
}

export type WebDavItemPickerOptions = WebDavItemPickerDisabledOptions | WebDavItemPickerEnabledOptions;

export interface WebDavFileSelection {
	kind: "webdav";
	items: readonly WebDavFile[];
}

export interface WebDavFolderSelection {
	kind: "webdav";
	item: WebDavFolder;
}

export type FileSelection = SharePointFileSelection | WebDavFileSelection;

export enum ItemPickerMode {
	SingleFolder = 1,
	MultipleFiles = 2,
	MultipleFilesOrOneNoteNotebooks = 3,
	SingleFileOrOneNoteNotebook = 4
}
